import { useEffect, useState } from 'react';

/**
 * Installing Realmery as an app (Chrome and Edge, on Android and desktop). The browser
 * says it may be installed with `beforeinstallprompt`, early and once, so it's caught as
 * soon as this module loads; the app bar then offers Install app. Browsers without the
 * event (Safari, Firefox) offer it from their own menus instead.
 */

interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let waiting: InstallPrompt | null = null;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((listener) => listener());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault(); // keep Chrome's own mini-bar from showing as well
    waiting = event as InstallPrompt;
    changed();
  });
  window.addEventListener('appinstalled', () => {
    waiting = null;
    changed();
  });
}

/** Whether the browser offers installing now, and the way to ask it. */
export function useInstall(): { canInstall: boolean; install: () => Promise<void> } {
  const [, rerender] = useState(0);
  useEffect(() => {
    const listener = () => rerender((n) => n + 1);
    listeners.add(listener);
    return () => void listeners.delete(listener);
  }, []);
  return {
    canInstall: waiting !== null,
    async install() {
      const prompt = waiting;
      if (!prompt) return;
      await prompt.prompt();
      // Each prompt asks once: after either answer, the browser may fire a new one later.
      waiting = null;
      changed();
    },
  };
}
